import { env } from "@/env";
import { cn } from "@/lib/utils";
import { useMarket } from "@app/_context";
import { getWalleyWidgetToken } from "@server/requests/checkout/getWalleyWidgetToken";
import { useQuery } from "@tanstack/react-query";
import { ComponentProps, useEffect, useRef } from "react";

function CartWalleyWidgetSkeleton({
  className,
  ...props
}: ComponentProps<"div">) {
  return (
    <div
      data-type="skeleton"
      className={cn(
        "flex h-[104px] animate-pulse flex-col gap-2 rounded-sm border bg-gray-100 p-2",
        className,
      )}
      {...props}
    >
      <div className="flex justify-between">
        <div className="h-4 w-32 animate-pulse bg-gray-200" />
        <div className="h-4 w-28 animate-pulse bg-gray-200" />
      </div>
      <div className="h-4 w-56 animate-pulse bg-gray-200" />
      <div className="h-8 w-20 animate-pulse bg-gray-200" />
    </div>
  );
}

type ProductPageWalleyWidgetScriptProps = {
  serviceLocale: string;
  walleyToken?: string;
  variantPrice?: number;
};

function CartWalleyWidgetScript({
  serviceLocale,
  walleyToken = "",
  variantPrice = 1,
  ...props
}: ProductPageWalleyWidgetScriptProps & ComponentProps<"div">) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const script = ref.current.querySelector("script");

    if (!script) return;

    script.setAttribute("data-amount", String(variantPrice * 100));

    window.requestAnimationFrame(() =>
      window.walley?.checkout?.api?.update?.(),
    );
  }, [variantPrice]);

  useEffect(() => {
    if (!ref.current || !env.NEXT_PUBLIC_WALLEY_SCRIPT_SOURCE) {
      return;
    }

    if (!ref.current.querySelector("script")) {
      const script = document.createElement("script");

      script.setAttribute("src", env.NEXT_PUBLIC_WALLEY_SCRIPT_SOURCE);
      script.setAttribute("data-token", walleyToken);
      script.setAttribute("data-widget", "part-payment");
      script.setAttribute("data-amount", String(variantPrice * 100));
      script.setAttribute("data-lang", serviceLocale);

      ref.current?.appendChild(script);

      window.walley?.checkout?.api?.update?.();
      return;
    }
  }, [walleyToken, serviceLocale, variantPrice]);

  return <div ref={ref} {...props} />;
}

export default function CartWalleyWidget({ cartTotal }: { cartTotal: number }) {
  const marketConfig = useMarket().state.market;
  const {
    data: walleyToken,
    isPending,
    isFetching,
  } = useQuery({
    queryKey: ["walleyToken"],
    queryFn: () => getWalleyWidgetToken(marketConfig.walleyB2CStoreId),
  });

  if (isPending || isFetching) {
    return <CartWalleyWidgetSkeleton className="mb-2" />;
  }

  if (!walleyToken) {
    return null;
  }

  return (
    <CartWalleyWidgetScript
      className="mb-2"
      walleyToken={walleyToken}
      serviceLocale={marketConfig.serviceLocale}
      variantPrice={cartTotal}
    />
  );
}
